import type { CSS } from '@stitches/react';
import { memo, useMemo } from 'react';

import { Select } from '@/components/shared/form/select';
import { DEFAULT_SEL_LABEL } from '@/contents/lcv/lcv-compare';
import { CvLineupModel } from '@/services/contentstack/models/cv-config';
import { LcvModel } from '@/services/contentstack/models/lcv-model';
import { LcvModelCategory } from '@/services/contentstack/models/lcv-model-category';

type CompareOption = Pick<
  LcvModelCategory,
  'uid' | 'name_en' | 'name_th' | 'register_url' | 'product_url'
> &
  Pick<CvLineupModel, 'category'>;

export interface CompareOptionGroup extends CompareOption {
  models: Pick<LcvModel, 'name' | 'key' | 'uid'>[];
}

interface CompareSelectProps {
  css?: CSS;
  name: string;
  index: number;
  value: string | Pick<LcvModel, 'uid'>;
  selectedParams: string[] | Pick<LcvModel, 'uid'>[];
  options: CompareOptionGroup[];
  onOptionChange: (index: number, param: string) => void;
}

export const CompareSelect: React.FC<CompareSelectProps> = memo(
  ({
    name,
    index,
    value,
    selectedParams,
    options,
    onOptionChange,
    css,
  }: CompareSelectProps) => {
    const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onOptionChange(index, e.target.value);
    };

    const selectedValue = useMemo(() => {
      if (typeof value === 'string') return value;
      return value?.uid;
    }, [value]);

    return (
      <Select
        css={css}
        name={name}
        value={selectedValue}
        onChange={handleModelChange}
      >
        <option value="">{DEFAULT_SEL_LABEL}</option>
        {options.map((group, groupIndex) => (
          <optgroup
            key={groupIndex}
            label={group.name_th || group.category[0].name}
          >
            {group.models.map((model, modelIndex) => {
              const value = model?.key || model?.uid;
              return (
                <option
                  key={modelIndex}
                  value={value}
                  disabled={selectedParams?.some(
                    (key) => key === value || key?.uid === value
                  )}
                >
                  {model.name}
                </option>
              );
            })}
          </optgroup>
        ))}
      </Select>
    );
  }
);

CompareSelect.displayName = 'CompareSelect';
